import React from "react";

import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import {
  Stack,
  IconButton,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useQuery } from "@tanstack/react-query";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import ButtonView from "../../../common/Button";
import { Column } from "../../../common/Table/types";
import { Data } from "../../types";
import TableView from "../../../common/Table";
import { projectsService } from "../../services";
import ProjectRow from "./parts/ProjectRow";
import { formatDateAndTime } from "../../../../utils/formatDate";
import RoleBasedAccess from "../../../common/RoleBasedAccess";
import { RoleName } from "../../../common/enums/RoleName";

const columns: Column[] = [
  { id: "name", label: "Name", minWidth: 100 },
  { id: "description", label: "Desciption", minWidth: 100 },
  { id: "created_at", label: "Created At", minWidth: 100 },
  { id: "number_of_episodes", label: "Number Of Episodes", minWidth: 100 },
  { id: "action", label: "Action", minWidth: 10 },
];

function createData(
  id: string,
  name: string,
  description: string,
  created_at: string,
  number_of_episodes: number,
): Data {
  return { id, name, description, created_at, number_of_episodes };
}

interface Props {
  handleProjectCreateDialog: () => void;
}

export default function ProjectsList({ handleProjectCreateDialog }: Props) {
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [isRefreshing, setIsRefreshing] = React.useState<boolean>(false);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["projects", page, rowsPerPage],
    queryFn: () =>
      projectsService.fetchProjects({
        page,
        perPage: rowsPerPage,
      }),
  });

  const rows =
    data?.projects?.map((project) => {
      return createData(
        project.id,
        project.name,
        project.description,
        formatDateAndTime(project.created_at),
        project.metadata.number_of_episodes,
      );
    }) || [];

  const handleChangePage = (_event: unknown, newPage: number) => {
    if (newPage === 0) {
      setPage(newPage + 1);
    }
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const startTime = Date.now();
      await refetch();

      // Ensure minimum delay of 500ms for better UX
      const elapsedTime = Date.now() - startTime;
      const minDelay = 500;
      if (elapsedTime < minDelay) {
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), minDelay - elapsedTime);
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          mb: 2,
          mt: { xs: 4, sm: 0 },
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 2 },
          borderRadius: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
          display: "flex",
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* Modern, responsive button row */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent={{ xs: "center", sm: "flex-end" }}
          sx={{ width: "100%", flexWrap: "nowrap", mb: 0 }}
        >
          <IconButton
            onClick={handleRefresh}
            size="small"
            disabled={isRefreshing}
            sx={{
              color: "primary.main",
              background: "#f5f7fa",
              borderRadius: "50%",
              width: 36,
              height: 36,
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              "&:hover": { background: "#e3e8ef" },
              "&:disabled": {
                background: "#f0f0f0",
                color: "#999",
              },
            }}
          >
            {isRefreshing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <RefreshIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
          <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
            <ButtonView
              type="button"
              variant="contained"
              size="small"
              label="Add Project"
              startIcon={<AddOutlinedIcon sx={{ fontSize: 12 }} />}
              handleOnClick={handleProjectCreateDialog}
              sx={{
                borderRadius: 999,
                px: 1.5,
                py: 0.5,
                fontSize: 12,
                height: 36,
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                textTransform: "none",
                fontWeight: 600,
                letterSpacing: 0.2,
              }}
            />
          </RoleBasedAccess>
        </Stack>
      </Paper>
      {/* Modern card-like table container with section title */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 1.5, sm: 3 },
          borderRadius: 4,
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          mb: 2,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Loading projects...
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 800, overflowX: "auto" }}>
              <TableView
                columns={columns}
                rows={rows}
                handleClick={() => {}}
                TableList={ProjectRow}
                handleCustomEvent={() => {}}
              />
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={data?.pagination.total_items || 0}
              rowsPerPage={rowsPerPage}
              page={page - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton
              showLastButton
            />
          </>
        )}
      </Paper>
    </>
  );
}
